import React, { useContext } from 'react';
import { Dropdown, Toggle, Label, DefaultButton } from '@fluentui/react';
import { ThemeContext } from '../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../../Common/util/localStorageUtils';
import { EnvironmentFlags, Pipeline, Region, WorkLane } from '../models/EnvironmentModels';

interface ModeConfigurationFieldsProps {
    isEnabled: boolean;
    pipeline: number | null | undefined;
    region: number | null | undefined;
    worklane: number | null | undefined;
    featureFlags: EnvironmentFlags;
    onIsEnabledChange: (checked: boolean) => void;
    onPipelineChange: (value: number | null) => void;
    onRegionChange: (value: number | null) => void;
    onWorklaneChange: (value: number | null) => void;
    onFeatureFlagChange: (flagKey: keyof EnvironmentFlags, value: boolean | null) => void;
    readOnly?: boolean;
    showModeConfig?: boolean;
}

export const ModeConfigurationFields: React.FC<ModeConfigurationFieldsProps> = ({
    isEnabled,
    pipeline,
    region,
    worklane,
    featureFlags,
    onIsEnabledChange,
    onPipelineChange,
    onRegionChange,
    onWorklaneChange,
    onFeatureFlagChange,
    readOnly = false,
    showModeConfig = true,
}) => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);

    const renderModeConfiguration = () => {
        if (!showModeConfig) return null;

        return (
            <>
                <Label styles={{ root: { fontWeight: 600, marginBottom: '8px' } }}>Mode Configuration</Label>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '12px',
                        padding: '12px',
                        backgroundColor: readOnly ? theme.palette.white : theme.palette.neutralLighterAlt,
                        border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                        marginBottom: '16px',
                        borderRadius: '4px',
                    }}
                >
                    {readOnly ? (
                        <div style={{ padding: '8px', backgroundColor: theme.palette.white, border: `1px solid ${theme.palette.neutralTertiaryAlt}`, borderRadius: '4px' }}>
                            <Toggle
                                label="Enabled"
                                checked={isEnabled}
                                styles={{
                                    root: { cursor: 'default', pointerEvents: 'none' },
                                }}
                            />
                        </div>
                    ) : (
                        <Toggle
                            label="Enabled"
                            checked={isEnabled}
                            onChange={(_, checked) => onIsEnabledChange(checked || false)}
                            styles={{ root: { padding: '8px' } }}
                        />
                    )}

                    <div
                        style={{
                            padding: readOnly ? '8px' : '0',
                            backgroundColor: readOnly ? theme.palette.white : 'transparent',
                            border: readOnly ? `1px solid ${theme.palette.neutralTertiaryAlt}` : 'none',
                            borderRadius: '4px',
                            pointerEvents: readOnly ? 'none' : 'auto',
                        }}
                    >
                        <Dropdown
                            label="Pipeline"
                            selectedKey={pipeline !== null && pipeline !== undefined ? pipeline : 'null'}
                            options={[
                                { key: 'null', text: 'Not Set' },
                                { key: 0, text: 'Blob' },
                                { key: 1, text: 'Core' },
                                { key: 2, text: 'Message' },
                            ]}
                            onChange={(_, option) => {
                                if (option && !readOnly) {
                                    onPipelineChange(option.key === 'null' ? null : (option.key as number));
                                }
                            }}
                            styles={{
                                root: { cursor: readOnly ? 'default' : 'pointer', padding: readOnly ? '0' : '8px' },
                            }}
                        />
                    </div>

                    <div
                        style={{
                            padding: readOnly ? '8px' : '0',
                            backgroundColor: readOnly ? theme.palette.white : 'transparent',
                            border: readOnly ? `1px solid ${theme.palette.neutralTertiaryAlt}` : 'none',
                            borderRadius: '4px',
                            pointerEvents: readOnly ? 'none' : 'auto',
                        }}
                    >
                        <Dropdown
                            label="Region"
                            selectedKey={region !== null && region !== undefined ? region : 'null'}
                            options={[
                                { key: 'null', text: 'Not Set' },
                                { key: 0, text: 'Primary' },
                                { key: 1, text: 'Secondary' },
                            ]}
                            onChange={(_, option) => {
                                if (option && !readOnly) {
                                    onRegionChange(option.key === 'null' ? null : (option.key as number));
                                }
                            }}
                            styles={{
                                root: { cursor: readOnly ? 'default' : 'pointer', padding: readOnly ? '0' : '8px' },
                            }}
                        />
                    </div>

                    <div
                        style={{
                            padding: readOnly ? '8px' : '0',
                            backgroundColor: readOnly ? theme.palette.white : 'transparent',
                            border: readOnly ? `1px solid ${theme.palette.neutralTertiaryAlt}` : 'none',
                            borderRadius: '4px',
                            pointerEvents: readOnly ? 'none' : 'auto',
                        }}
                    >
                        <Dropdown
                            label="Work Lane"
                            selectedKey={worklane !== null && worklane !== undefined ? worklane : 'null'}
                            options={[
                                { key: 'null', text: 'Not Set' },
                                { key: 0, text: 'Warm' },
                                { key: 1, text: 'Dcmt' },
                            ]}
                            onChange={(_, option) => {
                                if (option && !readOnly) {
                                    onWorklaneChange(option.key === 'null' ? null : (option.key as number));
                                }
                            }}
                            styles={{
                                root: { cursor: readOnly ? 'default' : 'pointer', padding: readOnly ? '0' : '8px' },
                            }}
                        />
                    </div>
                </div>
            </>
        );
    };

    const renderFeatureFlags = () => {
        const flagEntries = Object.entries(featureFlags ?? {}) as [keyof EnvironmentFlags, boolean | null | undefined][];

        return (
            <>
                <Label styles={{ root: { fontWeight: 600, marginBottom: '8px' } }}>Feature Flags</Label>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                        gap: '12px',
                        padding: readOnly ? '0' : '12px',
                        backgroundColor: readOnly ? 'transparent' : theme.palette.neutralLighterAlt,
                    }}
                >
                    {flagEntries.map(([key, value]) => {
                        const displayName = key.replace(/([A-Z])/g, ' $1').trim();
                        const capitalizedName = displayName.charAt(0).toUpperCase() + displayName.slice(1);

                        return (
                            <div
                                key={key}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '4px',
                                    padding: '10px',
                                    backgroundColor: theme.palette.white,
                                    border: `1px solid ${theme.palette.neutralLight}`,
                                    borderRadius: '4px',
                                }}
                            >
                                <Label>{capitalizedName}</Label>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <DefaultButton
                                        text="True"
                                        onClick={() => !readOnly && onFeatureFlagChange(key, true)}
                                        styles={{
                                            root: {
                                                minWidth: '60px',
                                                backgroundColor: value === true ? theme.palette.green : undefined,
                                                color: value === true ? 'white' : undefined,
                                                cursor: readOnly ? 'default' : 'pointer',
                                                pointerEvents: readOnly ? 'none' : 'auto',
                                            },
                                            rootHovered: {
                                                backgroundColor: value === true ? theme.palette.green : undefined,
                                                color: value === true ? 'white' : undefined,
                                            },
                                            rootPressed: {
                                                backgroundColor: value === true ? theme.palette.green : undefined,
                                                color: value === true ? 'white' : undefined,
                                            },
                                        }}
                                    />
                                    <DefaultButton
                                        text="False"
                                        onClick={() => !readOnly && onFeatureFlagChange(key, false)}
                                        styles={{
                                            root: {
                                                minWidth: '60px',
                                                backgroundColor: value === false ? theme.palette.redDark : undefined,
                                                color: value === false ? 'white' : undefined,
                                                cursor: readOnly ? 'default' : 'pointer',
                                                pointerEvents: readOnly ? 'none' : 'auto',
                                            },
                                            rootHovered: {
                                                backgroundColor: value === false ? theme.palette.redDark : undefined,
                                                color: value === false ? 'white' : undefined,
                                            },
                                            rootPressed: {
                                                backgroundColor: value === false ? theme.palette.redDark : undefined,
                                                color: value === false ? 'white' : undefined,
                                            },
                                        }}
                                    />
                                    <DefaultButton
                                        text="Not Set"
                                        onClick={() => !readOnly && onFeatureFlagChange(key, null)}
                                        styles={{
                                            root: {
                                                minWidth: '60px',
                                                backgroundColor: value === null ? theme.palette.neutralSecondary : undefined,
                                                color: value === null ? 'white' : undefined,
                                                cursor: readOnly ? 'default' : 'pointer',
                                                pointerEvents: readOnly ? 'none' : 'auto',
                                            },
                                            rootHovered: {
                                                backgroundColor: value === null ? theme.palette.neutralSecondary : undefined,
                                                color: value === null ? 'white' : undefined,
                                            },
                                            rootPressed: {
                                                backgroundColor: value === null ? theme.palette.neutralSecondary : undefined,
                                                color: value === null ? 'white' : undefined,
                                            },
                                        }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </>
        );
    };

    return (
        <>
            {renderModeConfiguration()}
            {renderFeatureFlags()}
        </>
    );
};
